/* This example requires Tailwind CSS v2.0+ */
import { Dialog, Transition } from '@headlessui/react';
import { XIcon } from '@heroicons/react/solid';
import { Fragment, useEffect, useState } from 'react';

import clsxm from '@/lib/clsxm';

export default function Modal({
  showModal,
  onClose,
  children,
  className,
  doNotCloseOnOverlayClick = false,
  hideCloseButton = false,
  style,
  initialFocus,
}: {
  showModal: boolean;
  onClose: () => void;
  children: React.ReactNode;
  className?: string;
  doNotCloseOnOverlayClick?: boolean;
  hideCloseButton?: boolean;
  style?: React.CSSProperties;
  /** Element to focus on open (headlessui focuses the first focusable otherwise). */
  initialFocus?: React.MutableRefObject<HTMLElement | null>;
}) {
  // Safety: force-unmount the Dialog if the leave transition gets stuck.
  // Without this, a stuck transition leaves an invisible fixed overlay
  // that blocks all page interactions.
  const [rendered, setRendered] = useState(showModal);

  useEffect(() => {
    if (showModal) {
      setRendered(true);
    } else {
      const timeout = setTimeout(() => setRendered(false), 400);
      return () => clearTimeout(timeout);
    }
  }, [showModal]);

  if (!rendered && !showModal) return null;

  return (
    <Transition.Root
      show={showModal}
      as={Fragment}
      afterLeave={() => setRendered(false)}
    >
      <Dialog
        as='div'
        className='relative z-[999]'
        onClose={doNotCloseOnOverlayClick ? () => {} : onClose}
        initialFocus={initialFocus}
      >
        <Transition.Child
          as={Fragment}
          enter='ease-out duration-300'
          enterFrom='opacity-0'
          enterTo='opacity-100'
          leave='ease-in duration-200'
          leaveFrom='opacity-100'
          leaveTo='opacity-0'
        >
          <div className='fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity' />
        </Transition.Child>

        <div className='fixed inset-0 z-50 overflow-y-auto'>
          <div className='flex min-h-full items-end justify-center text-center sm:items-center sm:p-0'>
            <Transition.Child
              as={Fragment}
              enter='ease-out duration-300'
              enterFrom='opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95'
              enterTo='opacity-100 translate-y-0 sm:scale-100'
              leave='ease-in duration-200'
              leaveFrom='opacity-100 translate-y-0 sm:scale-100'
              leaveTo='opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95'
            >
              <Dialog.Panel
                className={clsxm(
                  'relative min-h-[full] w-[90%] transform overflow-hidden rounded-lg bg-white text-start shadow-xl transition-all dark:bg-slate-900 sm:w-full sm:max-w-2xl',
                  className,
                )}
                style={style}
              >
                {!hideCloseButton && (
                  <div className='absolute end-0 top-0  z-50 block pe-4 pt-4'>
                    <button
                      type='button'
                      className='rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:bg-slate-700'
                      onClick={onClose}
                    >
                      <span className='sr-only'>Close</span>
                      <XIcon className='h-6 w-6' aria-hidden='true' />
                    </button>
                  </div>
                )}

                <div className=''>{children}</div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
